import {
  TrackInfo,
  TrackType,
  AdPlayer,
  AdState,
  RequestType,
  SeekRangeInfo,
  StreamTimelineEvent,
  Action,
  StreamTimelineMetadata,
  ThumbnailPreviewMetadata,
  PlaybackOptions,
  AdBreakInfo,
  AdInfo,
  AdEvent,
  AdTrackingEvent,
  PLAYBACK_STATES,
  BUFFERING_STATES,
  SEEKING_STATES,
  TrackVariantInfo,
  AdTechnology,
  AdContentTimePosition,
} from '@quickplay/player-interface';

import { Error, Logger, createEventEmitter, EventEmitter, LoggerLevel, createLogger } from '@quickplay/foundation';

import { PlayerBuilder } from '../../../PlayerBuilder';
import {
  createCsaiAdBreakInfo,
  createCsaiAdInfo,
  formatStreamTimelineMetadata,
  updateAdBreakAfterInterruption,
} from '../../util/AdUtil';

import { QPAdsLoader } from './QPAdsLoader';
import { AdsManagerRole, QPAdsManager } from './QPAdsManager';
import { AFCPlaybackSession } from '@quickplay/player';
import { AFCComposedAdPlayer } from '../../../models/AFCComposedCsaiAdPlayer';

function onContentComplete(): void {
  QPAdsLoader.getSharedAdsLoader()?.contentComplete?.();
}

/**
 * Creates an IMA CSAI AdPlayer.
 */
export function createAdvancedCsaiAdsPlayer(builder: PlayerBuilder): AdPlayer {
  const logger: Logger = builder.configuredLogger ?? createLogger(LoggerLevel.OFF);

  const eventEmitter: EventEmitter = createEventEmitter();

  const { videoElement, imaSDK, renderingSettings, player: contentPlayer } = builder;
  const playbackOptions: PlaybackOptions | undefined = contentPlayer?.playbackOptions;
  let adFrequencySession: AFCPlaybackSession | undefined;

  let qpAdsManager: QPAdsManager | null = null;
  let adState: AdState = 'idle';
  let currentTime = 0;
  let duration = 0;
  let isBuffering = false;
  let isAdBreakStarted = false;
  let completedAdsDuration = 0;
  const initialCuePoints = new Set<number>();
  const watchedCuePoints = new Set<number>();

  let activeAdBreakInfo: AdBreakInfo | undefined;
  let activeAdInfo: AdInfo | undefined;
  let nonFatalAllAdsCompletedEvent = false;

  const isContinueWatchingSession = (): boolean =>
    Boolean(playbackOptions?.initialPlaybackTime && playbackOptions.initialPlaybackTime > 0);

  const shouldSkipPreroll = (): boolean =>
    isContinueWatchingSession() && renderingSettings?.playAdsAfterTime !== undefined;

  const AD_PLAYER_STATE_MAP: Readonly<Partial<Record<AdState, string>>> = {
    loading: PLAYBACK_STATES.LOADING,
    loaded: PLAYBACK_STATES.LOADED,
    paused: PLAYBACK_STATES.PAUSED,
    playing: PLAYBACK_STATES.STARTED,
    started: PLAYBACK_STATES.STARTED,
    resumed: PLAYBACK_STATES.STARTED,
    skipped: PLAYBACK_STATES.STARTED,
    completed: PLAYBACK_STATES.STARTED,
    failed: PLAYBACK_STATES.STARTED,
  };

  const adsManagerEventTypes = [
    google.ima.AdEvent.Type.ALL_ADS_COMPLETED,
    google.ima.AdEvent.Type.COMPLETE,
    google.ima.AdEvent.Type.FIRST_QUARTILE,
    google.ima.AdEvent.Type.LOADED,
    google.ima.AdEvent.Type.MIDPOINT,
    google.ima.AdEvent.Type.PAUSED,
    google.ima.AdEvent.Type.STARTED,
    google.ima.AdEvent.Type.RESUMED,
    google.ima.AdEvent.Type.AD_BUFFERING,
    google.ima.AdEvent.Type.AD_PROGRESS,
    google.ima.AdEvent.Type.THIRD_QUARTILE,
    google.ima.AdEvent.Type.SKIPPED,
    google.ima.AdEvent.Type.SKIPPABLE_STATE_CHANGED,
    google.ima.AdEvent.Type.LOG,
    google.ima.AdEvent.Type.CLICK,
    google.ima.AdEvent.Type.VIDEO_CLICKED,
    google.ima.AdEvent.Type.VIDEO_ICON_CLICKED,
    google.ima.AdEvent.Type.IMPRESSION,
    google.ima.AdEvent.Type.USER_CLOSE,
    google.ima.AdEvent.Type.INTERACTION,
    google.ima.AdEvent.Type.VOLUME_CHANGED,
    google.ima.AdEvent.Type.VOLUME_MUTED,
    google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED,
    google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
    google.ima.AdErrorEvent.Type.AD_ERROR,
    google.ima.AdEvent.Type.AD_BREAK_READY,
  ];

  function getActiveAdsManager(): google.ima.AdsManager | null {
    return qpAdsManager?.getActiveAdsManager?.() ?? null;
  }

  function publishUnwatchedCuePoints(): void {
    const cuePoints = Array.from(initialCuePoints).filter((cuePoint: number) => !watchedCuePoints.has(cuePoint));

    eventEmitter.publish(AdEvent.ADVERT_CUEPOINTS_UPDATED, cuePoints);
  }

  /**
   * Finds the first unwatched cue point that is after the playhead.
   *
   * @param playheadTime Current playback position in seconds.
   * @returns The cue point in seconds, or `undefined` if the time is invalid or no unwatched cue point is ahead.
   */
  function findNextUnwatchedCuePoint(playheadTime: number): number | undefined {
    if (!Number.isFinite(playheadTime)) {
      return undefined;
    }
    const allCuePoints = getActiveAdsManager()?.getCuePoints?.() ?? [];

    /** Returns the cue points (in seconds) that have not been played yet. */
    const unwatchedCuePoints = allCuePoints.filter(cuePoint => !watchedCuePoints.has(cuePoint));

    /** Returns the cue points (in seconds) that are after the current playhead. */
    const upcomingCuePoints = unwatchedCuePoints.filter(cuePoint => cuePoint > playheadTime);

    return upcomingCuePoints.length > 0 ? Math.min(...upcomingCuePoints) : undefined;
  }

  /**
   * Marks the cue point corresponding to the given ad break as watched.
   * @param adBreakInfo Information about the ad break that has been watched.
   */
  function markAdBreakCuePointWatched(adBreakInfo: AdBreakInfo): void {
    const startOffsetMs = adBreakInfo.adBreakStartTimeOffsetMs;
    logger.log('marking ad break cue point as watched', startOffsetMs / 1000);

    if (typeof startOffsetMs === 'number' && Number.isFinite(startOffsetMs)) {
      watchedCuePoints.add(startOffsetMs / 1000);
    }
  }

  /**
   * Marks the next unwatched cue point as watched because the ad break failed.
   */
  function markNextUnwatchedCuePointAsWatched(): void {
    const nextCuePoint = findNextUnwatchedCuePoint(contentPlayer.currentTime);
    logger.log('marked next unwatched cue point as watched because the ad break failed', nextCuePoint);
    if (nextCuePoint !== undefined) {
      watchedCuePoints.add(nextCuePoint);
    }
  }

  function finalizeCuePointWatchState(adBreakInfo?: AdBreakInfo): void {
    if (adBreakInfo) markAdBreakCuePointWatched(adBreakInfo);

    // Publish unwatched cue points only after non-preroll ad breaks.
    // Preroll cue points are published when the content AdsManager is activated.
    if (adBreakInfo?.contentTimePosition !== AdContentTimePosition.PREROLL) publishUnwatchedCuePoints();
    activeAdBreakInfo = undefined;
  }

  const UNPUBLISHED_AD_PLAYER_STATES: ReadonlySet<string> = new Set([PLAYBACK_STATES.LOADING, PLAYBACK_STATES.LOADED]);

  function getPlayerState(): string {
    return AD_PLAYER_STATE_MAP[adState] ?? PLAYBACK_STATES.IDLE;
  }

  function logStateChange(player: string, buffering: string): void {
    logger.info('Player state has changed', JSON.stringify({ player, seeking: SEEKING_STATES.INACTIVE, buffering }));
  }

  function publishPlaybackState(): void {
    const playerState = getPlayerState();

    if (UNPUBLISHED_AD_PLAYER_STATES.has(playerState)) {
      return;
    }

    logStateChange(playerState, BUFFERING_STATES.INACTIVE);
    eventEmitter.publish('playbackstatechanged', playerState);
  }

  function publishBufferingState(): void {
    const buffering = isBuffering ? BUFFERING_STATES.ACTIVE : BUFFERING_STATES.INACTIVE;

    logStateChange(getPlayerState(), buffering);
    eventEmitter.publish('bufferingstatechanged', buffering);
  }

  function updateAdPlayerState(state: AdState, error?: unknown): void {
    adState = state;

    if (state === 'buffering') {
      publishBufferingState();
      return;
    }

    if (state === 'failed') {
      logger.log('aderror', error);
    }

    publishPlaybackState();
  }

  function bindManagerListeners(manager: QPAdsManager): void {
    for (const eventType of adsManagerEventTypes) {
      manager.addEventListener(eventType, onAdsManagerEvent, false);
    }
  }

  function unbindManagerListeners(manager: QPAdsManager | null): void {
    if (!manager) {
      return;
    }

    for (const eventType of adsManagerEventTypes) {
      manager.removeEventListener(eventType, onAdsManagerEvent, false);
    }
  }

  function attachContentListeners(): void {
    videoElement.addEventListener('ended', onContentComplete);
  }

  function detachContentListeners(): void {
    videoElement.removeEventListener('ended', onContentComplete);
  }

  function getAdBreakInfo(adEvent: any): AdBreakInfo {
    return createCsaiAdBreakInfo(adEvent, getActiveAdsManager());
  }

  function getAdInfo(adEvent: any, adBreakInfo: AdBreakInfo): AdInfo {
    return createCsaiAdInfo(adEvent, getActiveAdsManager(), adBreakInfo);
  }

  function syncActiveAdInfoFromAd(ad: google.ima.Ad | null): void {
    if (!ad || !activeAdInfo) {
      return;
    }

    activeAdInfo.mediaUrl = ad.getMediaUrl?.() || '';

    if (activeAdInfo.vastProperties) {
      activeAdInfo.vastProperties.bitrate = ad.getVastMediaBitrate?.() || 0;
    }
  }

  function handleAdLoaded(adEvent: google.ima.AdEvent): void {
    activeAdBreakInfo = getAdBreakInfo(adEvent);
    activeAdInfo = getAdInfo(adEvent, activeAdBreakInfo);
    duration = activeAdInfo.durationMs || 0;
    updateAdPlayerState('loaded');
  }

  function handleAdStarted(adEvent: google.ima.AdEvent): void {
    nonFatalAllAdsCompletedEvent = false;
    isBuffering = false;

    eventEmitter.publish('bufferingstatechanged', BUFFERING_STATES.INACTIVE);

    const ad = adEvent.getAd?.();
    syncActiveAdInfoFromAd(ad);

    updateAdPlayerState('started');
    eventEmitter.publish(AdEvent.ADVERT_START, activeAdInfo);
  }

  function handleAdResumed(): void {
    isBuffering = false;
    updateAdPlayerState('resumed');
    onAdTrackingEvent(AdTrackingEvent.AD_RESUMED_EVENT);
  }

  function handleAdPaused(): void {
    updateAdPlayerState('paused');
  }

  function handleAdBuffering(): void {
    isBuffering = true;
    updateAdPlayerState('buffering');
  }

  function handleAdProgress(adEvent: any): void {
    const adData: google.ima.AdProgressData = adEvent.getAdData?.();

    if (!adData) {
      logger.log('Ad progress event received without ad data; skipping countdown update.');
      return;
    }

    const { totalAds, adPosition, duration, currentTime: adCurrentTime } = adData;

    const isLastAd = totalAds === adPosition;
    const adRemainingMs = (getActiveAdsManager()?.getRemainingTime?.() || 0) * 1000;

    currentTime = adCurrentTime;

    if (activeAdBreakInfo) {
      if (isLastAd) {
        activeAdBreakInfo.durationMs = duration * 1000;
        activeAdBreakInfo.remainingTimeMs = adRemainingMs;
        completedAdsDuration = 0;
      } else {
        const adBreakPlayedTimeMs = completedAdsDuration + adCurrentTime * 1000;

        activeAdBreakInfo.remainingTimeMs = Math.max(0, activeAdBreakInfo.durationMs - adBreakPlayedTimeMs);
      }
    }

    if (activeAdInfo) {
      activeAdInfo.adBreakInfo = activeAdBreakInfo;
      activeAdInfo.remainingTimeMs = adRemainingMs;
    }

    eventEmitter.publish('adprogressupdate', activeAdInfo, currentTime);
  }

  function handleFirstQuartile(adEvent: google.ima.AdEvent): void {
    const ad = adEvent.getAd?.();

    if (activeAdBreakInfo && ad) {
      activeAdBreakInfo.totalAds = ad.getAdPodInfo?.()?.getTotalAds?.() || activeAdBreakInfo.totalAds;
    }

    onAdTrackingEvent(AdTrackingEvent.AD_FIRST_QUARTILE_EVENT);
  }

  function handleAdComplete(): void {
    completedAdsDuration = completedAdsDuration + (activeAdInfo?.durationMs || 0);
    nonFatalAllAdsCompletedEvent = true;
    eventEmitter.publish(AdEvent.ADVERT_END, activeAdInfo);
  }

  function handleAllAdsCompleted(): void {
    if (nonFatalAllAdsCompletedEvent) {
      return;
    }

    completeAdBreak();
  }

  function handleAdBreakReady(): void {
    const shouldStartAd = adFrequencySession?.evaluateOpportunity() ?? true;
    if (!shouldStartAd) {
      eventEmitter.publish(AdEvent.ADVERT_SKIP_AFC_POLICY, activeAdBreakInfo);

      finalizeCuePointWatchState(activeAdBreakInfo);
      logger.debug(
        '[IMA CSAI] Ad break skipped due to ad frequency session evaluation and marking cue point as watched',
      );
      return;
    }
    qpAdsManager?.start();
  }

  function handleAdSkipped(): void {
    if (activeAdInfo && activeAdBreakInfo) {
      completedAdsDuration = updateAdBreakAfterInterruption(activeAdInfo, activeAdBreakInfo, completedAdsDuration);
    }

    updateAdPlayerState('skipped');
    onAdTrackingEvent(AdTrackingEvent.AD_SKIPPED_EVENT);
    activeAdInfo = undefined;
  }

  function handleAdLog(adEvent: any): void {
    const adError = adEvent.getAdData?.()?.adError;

    if (!adError) {
      return;
    }

    logger.log('aderror', adError);
    onAdNonFatalError(adError);
  }

  function onAdTrackingEvent(event: AdTrackingEvent): void {
    eventEmitter.publish(AdEvent.ADVERT_TRACKING_EVENT, event);
  }

  function onAdEvent(adEvent: google.ima.AdEvent): void {
    switch (adEvent?.type) {
      case google.ima.AdEvent.Type.AD_BREAK_READY:
        handleAdBreakReady();
        return;
      case google.ima.AdEvent.Type.LOADED:
        handleAdLoaded(adEvent);
        return;
      case google.ima.AdEvent.Type.STARTED:
        handleAdStarted(adEvent);
        return;
      case google.ima.AdEvent.Type.RESUMED:
        handleAdResumed();
        return;
      case google.ima.AdEvent.Type.PAUSED:
        handleAdPaused();
        return;
      case google.ima.AdEvent.Type.AD_BUFFERING:
        handleAdBuffering();
        return;
      case google.ima.AdEvent.Type.AD_PROGRESS:
        handleAdProgress(adEvent);
        return;
      case google.ima.AdEvent.Type.FIRST_QUARTILE:
        handleFirstQuartile(adEvent);
        return;
      case google.ima.AdEvent.Type.MIDPOINT:
        onAdTrackingEvent(AdTrackingEvent.AD_MIDPOINT_EVENT);
        return;
      case google.ima.AdEvent.Type.THIRD_QUARTILE:
        onAdTrackingEvent(AdTrackingEvent.AD_THIRD_QUARTILE_EVENT);
        return;
      case google.ima.AdEvent.Type.COMPLETE:
        handleAdComplete();
        return;
      case google.ima.AdEvent.Type.ALL_ADS_COMPLETED:
        handleAllAdsCompleted();
        return;
      case google.ima.AdEvent.Type.SKIPPED:
        handleAdSkipped();
        return;
      case google.ima.AdEvent.Type.LOG:
        handleAdLog(adEvent);
        return;
      case google.ima.AdEvent.Type.CLICK:
        onAdTrackingEvent(AdTrackingEvent.AD_CLICK);
        return;
      case google.ima.AdEvent.Type.VIDEO_CLICKED:
        onAdTrackingEvent(AdTrackingEvent.AD_VIDEO_CLICKED);
        return;
      case google.ima.AdEvent.Type.VIDEO_ICON_CLICKED:
        onAdTrackingEvent(AdTrackingEvent.AD_VIDEO_ICON_CLICKED);
        return;
      case google.ima.AdEvent.Type.IMPRESSION:
        onAdTrackingEvent(AdTrackingEvent.AD_IMPRESSION);
        return;
      case google.ima.AdEvent.Type.USER_CLOSE:
        onAdTrackingEvent(AdTrackingEvent.AD_USER_CLOSE);
        return;
      case google.ima.AdEvent.Type.INTERACTION:
        onAdTrackingEvent(AdTrackingEvent.AD_INTERACTION);
        return;
      case google.ima.AdEvent.Type.VOLUME_CHANGED:
        onAdTrackingEvent(AdTrackingEvent.AD_VOLUME_CHANGED);
        return;
      case google.ima.AdEvent.Type.VOLUME_MUTED:
        onAdTrackingEvent(AdTrackingEvent.AD_VOLUME_MUTED);
        return;
      case google.ima.AdEvent.Type.SKIPPABLE_STATE_CHANGED:
        onAdTrackingEvent(AdTrackingEvent.AD_SKIPPABLE_STATE_CHANGED);
        return;
      default:
        return;
    }
  }

  function onAdsManagerEvent(event: any): void {
    switch (event?.type) {
      case google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED:
        onContentPauseRequested();
        return;
      case google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED:
        onContentResumeRequested();
        return;
      case google.ima.AdErrorEvent.Type.AD_ERROR:
        onAdError(event);
        return;
      default:
        onAdEvent(event);
    }
  }

  function completeAdBreak(): void {
    updateAdPlayerState('completed');
    activeAdInfo = undefined;
  }

  function onContentPauseRequested(): void {
    isAdBreakStarted = true;
    eventEmitter.publish(AdEvent.ADVERT_BREAK_START, activeAdBreakInfo);
    eventEmitter.publish('adstatechanged', true);
    eventEmitter.publish('adplayercontextwillchange');
  }

  function onContentResumeRequested(): void {
    if (isAdBreakStarted) {
      eventEmitter.publish(AdEvent.ADVERT_BREAK_END, activeAdBreakInfo);
      isAdBreakStarted = false;
    }
    completedAdsDuration = 0;
    isBuffering = false;
    publishBufferingState();
    finalizeCuePointWatchState(activeAdBreakInfo);
    eventEmitter.publish('adstatechanged', false);
    eventEmitter.publish('adplayercontextwillchange');
  }

  function onAdNonFatalError(adError: google.ima.AdError): void {
    logger.log('[IMA CSAI] Non-fatal ad error', adError);
    markNextUnwatchedCuePointAsWatched();
    publishUnwatchedCuePoints();
    updateAdPlayerState('failed', adError);
    eventEmitter.publish(AdEvent.ADVERT_ERROR, adError);
  }

  function onAdError(adErrorEvent: any): void {
    const adError = adErrorEvent?.getError?.() || adErrorEvent;

    logger.debug('[IMA CSAI] Ad error occurred:', adError);
    if (activeAdInfo && activeAdBreakInfo) {
      completedAdsDuration = updateAdBreakAfterInterruption(activeAdInfo, activeAdBreakInfo, completedAdsDuration);
    }
    updateAdPlayerState('failed', adError);
    eventEmitter.publish(AdEvent.ADVERT_ERROR, adError);
  }

  function destroyAdsManager(): void {
    if (!qpAdsManager) {
      return;
    }

    unbindManagerListeners(qpAdsManager);

    try {
      qpAdsManager.destroy();
    } catch (error) {
      logger.error(`[IMA CSAI] AdsManager cleanup failed: ${error}`);
    } finally {
      qpAdsManager = null;
    }
  }

  function cleanup(): void {
    unbindManagerListeners(qpAdsManager);
    destroyAdsManager();
    detachContentListeners();

    activeAdInfo = undefined;
    activeAdBreakInfo = undefined;
    currentTime = 0;
    duration = 0;
    isBuffering = false;
    isAdBreakStarted = false;
    nonFatalAllAdsCompletedEvent = false;
    watchedCuePoints.clear();
    initialCuePoints.clear();
  }

  function requestAds(): void {
    const AdsManagerRequest = {
      containerElement: builder.containerElement,
      videoElement,
      imaSDK,
      imaSdkSettings: builder.imaSettings,
      request: builder.request,
      renderingSettings,
      configuredLogger: logger,
      onAdError,
      isPrefetchEnabled: builder.isPrefetchEnabled,
      adFrequencySession,
      onAdsManagerActivated,
    };

    Promise.resolve(QPAdsLoader.createComposedQPAdsManager(AdsManagerRequest))
      .then(onQPAdsManagerLoaded)
      .catch(error => {
        logger.error('[IMA CSAI] Failed to create QPAdsManager:', error);
      });
  }

  function onQPAdsManagerLoaded(adsManager: QPAdsManager): void {
    destroyAdsManager();
    qpAdsManager = adsManager;
    bindManagerListeners(qpAdsManager);

    if (shouldSkipPreroll()) {
      logger.log('[IMA CSAI] Continue Watching - AdsManager loaded without pre-roll start');
      updateAdPlayerState('loaded');
    }
  }
  function onAdsManagerActivated(role?: AdsManagerRole): void {
    const cuePoints = getActiveAdsManager()?.getCuePoints?.() ?? [];

    if (role === 'PREROLL') {
      logger.log('[IMA CSAI] Pre-roll AdsManager activated');
      initialCuePoints.add(0);
      return;
    }
    cuePoints.forEach((cuePoint: number) => initialCuePoints.add(cuePoint));

    eventEmitter.publish(AdEvent.ADVERT_CUEPOINTS, Array.from(initialCuePoints));
    publishUnwatchedCuePoints();
  }

  const imaPlayer: AFCComposedAdPlayer = {
    subscribe: eventEmitter.subscribe,
    unsubscribe: eventEmitter.unsubscribe,
    setAdFrequencySession(session: AFCPlaybackSession): void {
      adFrequencySession = session;
    },

    get playerState() {
      return getPlayerState();
    },

    get isBuffering() {
      return isBuffering;
    },

    get isSeeking() {
      return false;
    },

    get currentTime() {
      return currentTime;
    },

    get currentEpochTime() {
      return 0;
    },

    get duration() {
      return duration;
    },

    get playbackRate() {
      return 0;
    },

    get availableBitrates() {
      return [];
    },

    get minBitrate() {
      return 0;
    },

    get maxBitrate() {
      return 0;
    },

    get internalPlayerVersion() {
      return imaSDK.VERSION;
    },

    get internalPlayerName() {
      return 'IMA SDK';
    },

    get isLive() {
      return false;
    },

    get remainingTime() {
      return getActiveAdsManager()?.getRemainingTime?.() ?? 0;
    },

    get isMute() {
      return !(getActiveAdsManager()?.getVolume?.() ?? 0);
    },

    get volume() {
      return getActiveAdsManager()?.getVolume?.() ?? 0;
    },

    set volume(value: number) {
      getActiveAdsManager()?.setVolume?.(value);
    },

    get adInsertionType() {
      return AdTechnology.CSAI;
    },

    load(): void {
      updateAdPlayerState('loading');
      requestAds();
    },

    play(): void {
      getActiveAdsManager()?.resume?.();
    },

    pause(): void {
      getActiveAdsManager()?.pause?.();
    },

    abort(_error: Error): void {
      cleanup();
    },

    seek(seekPoint: number): void {
      contentPlayer.seek(seekPoint);
    },

    getThumbnail(_position: number): Promise<ThumbnailPreviewMetadata | undefined> {
      return Promise.resolve(undefined);
    },

    getAllTracks(): TrackInfo[] {
      return [];
    },

    getActiveTracks(): TrackInfo[] {
      return [];
    },

    getVariantTracks(): TrackVariantInfo[] {
      return [];
    },

    getAvailableTrackVariants(_trackType: TrackType): TrackVariantInfo[] {
      return [];
    },

    seekableRange(): SeekRangeInfo {
      return {
        start: 0,
        end: duration,
      };
    },

    seekToLiveEdge(): void {
      // IMA CSAI is not a live player.
    },

    currentOffsetFromLiveEdge(): number {
      return 0;
    },

    selectTrack(_track?: TrackInfo): void {
      // Not supported by IMA CSAI player.
    },

    stop(): Promise<void> {
      logger.debug('[IMA CSAI] stop called');
      cleanup();
      updateAdPlayerState('idle');
      return Promise.resolve();
    },

    muteOrUnmute(): void {
      if (!qpAdsManager) {
        return;
      }

      const activeAdsManager = getActiveAdsManager();

      if (!activeAdsManager) {
        return;
      }

      const volume = activeAdsManager.getVolume?.() ?? 0;
      activeAdsManager.setVolume?.(volume ? 0 : videoElement.volume);
    },

    skipAd(): void {
      const activeAdsManager = getActiveAdsManager();

      if (!activeAdsManager) {
        return;
      }

      updateAdPlayerState('skipped');
      activeAdsManager.skip?.();
    },

    resize(width: number, height: number, isFullscreen: boolean): void {
      getActiveAdsManager()?.resize?.(
        width,
        height,
        isFullscreen ? imaSDK.ViewMode.FULLSCREEN : imaSDK.ViewMode.NORMAL,
      );
    },

    discardAdBreak(): void {
      getActiveAdsManager()?.discardAdBreak?.();
    },

    getCuePoints(): number[] {
      return initialCuePoints.size > 0 ? Array.from(initialCuePoints) : (getActiveAdsManager()?.getCuePoints?.() ?? []);
    },

    setRequestInterceptor(_requestInterceptor: (requestType: RequestType, request: any) => void): void {
      // IMA request interception is handled elsewhere.
    },

    setResponseInterceptor(_responseInterceptor: (requestType: RequestType, response: any) => void): void {
      // IMA response interception is handled elsewhere.
    },

    restrictQuality(_enableRestriction: boolean): void {
      // Not supported by IMA CSAI.
    },

    availableMimeTypes(_track: TrackType): string[] {
      return [];
    },

    unloadAndSavePreload(): Promise<void> {
      return Promise.resolve();
    },

    restoreFromPreload(): void {
      // Not supported by IMA CSAI.
    },

    setPreferredMimeTypes(_trackType: TrackType, _mimeTypes: string[]): void {
      // Not supported by IMA CSAI.
    },

    accessFunctionHandler(_functionName: string): void {
      // No-op.
    },

    send(eventName: StreamTimelineEvent, action: Action, metadata?: StreamTimelineMetadata): void {
      logger.log(
        `streamTimelineEvent: ${eventName} Action: ${action} metadata: ${formatStreamTimelineMetadata(metadata)}`,
      );
    },

    setPlaybackOptions(options: PlaybackOptions): void {
      logger.log('playback options', options);
    },
  };

  attachContentListeners();

  return imaPlayer;
}
